import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { IUser, UserTypes } from '@/features/user/store/user.model';
import { USResident } from '@/features/user/components/CreateUser/USResident';
import { ITaxResidenceCreate } from '@/features/user/contexts/CreateUserContext';
import { TKycSection } from '@/features/kyc/components/userInformation/InformationSection';
import { useUpdateTaxResidences } from '@/features/taxResidences/hooks/useUpdateTaxResidences';
import { CreateUserTaxResidences } from '@/features/user/components/CreateUser/CreateUserTaxResidences';

export function useTaxResidencesEditionProps(user: IUser) {
  const { t } = useTranslation();

  const {
    handleUpdateTaxResidences,
    watch,
    handleValueChange,
    shouldBeUSResident,
    onCancelEdition,
    isLoading,
    isTaxResidenceDisabled,
  } = useUpdateTaxResidences(user);

  const specifiedUSPersonWatch = watch('specifiedUSPerson');
  const taxResidencesWatch = watch('taxResidences');
  const isBusiness = user?.userTypeId === UserTypes.business;

  const taxResidenceData = useMemo(
    () =>
      ({
        subTitle: t('pages.currentProfiles.profile.taxResidence.cardTitle'),
        categories: [
          {
            component: (
              <>
                <USResident
                  title={t('pages.dashboard.createUser.form.isCompanyUsTaxResident')}
                  description={t('pages.dashboard.createUser.form.usCompanyTaxResidentDescription')}
                  value={specifiedUSPersonWatch.toString()}
                  handleValuesChange={(changes) => changes.map(({ value, key }) => handleValueChange(value, key))}
                  taxResidences={taxResidencesWatch}
                  shouldShowWarning={
                    shouldBeUSResident(['country', 'secondaryCountry']) && specifiedUSPersonWatch?.toString() !== '1'
                  }
                />
                <CreateUserTaxResidences
                  taxResidences={taxResidencesWatch}
                  defaultCountry={user?.country}
                  onUpdate={(value: ITaxResidenceCreate[]) => handleValueChange(value, 'taxResidences')}
                  size="s"
                  isBusiness={isBusiness}
                />
              </>
            ),
          },
        ],
      }) as TKycSection,
    [handleValueChange, shouldBeUSResident, specifiedUSPersonWatch, t, taxResidencesWatch, user?.country, isBusiness],
  );

  const isFormDisabled = isTaxResidenceDisabled(taxResidencesWatch, isBusiness);

  return { taxResidenceData, handleUpdateTaxResidences, onCancelEdition, isLoading, isFormDisabled };
}
